/**
 * StopReasonBanner — Phase 2 / Task 6 of the unified research pipeline plan.
 *
 * Renders a copy-only banner above the research results that distinguishes
 * the four pipeline stop_reason states. Critical for the new
 * `max_evaluated` case: that result set is partial-by-construction
 * (the pipeline ran out of evaluation budget before exhausting the
 * candidate pool), which the user MUST be able to tell apart from
 * `max_matches` (got enough good answers).
 *
 * Spec lives in the plan's "End-to-end surfacing" subsection of the
 * Evaluation budget section. This is the ONLY in-scope frontend
 * change for Phase 2; the per-drug results UI is unchanged.
 *
 * Data flow:
 *   research_pipeline.PipelineResult.stop_reason
 *     → ResearchJob.metadata_json["stop_reason"]
 *     → ResearchJobResponse.stop_reason  (research_routes.py)
 *     → this component
 *
 * Both `stop_reason` and `total_evaluated` are optional — legacy job
 * rows that pre-date the metadata stamp at research_service.py:949
 * surface `null`, in which case we render nothing.
 */
import React from 'react';

type StopReason =
  | 'completed'
  | 'max_matches'
  | 'max_evaluated'
  | 'time_budget_exceeded'
  | 'cancelled'
  | 'no_candidates'
  | 'cohort_too_small';

interface StopReasonBannerProps {
  // ``| undefined`` explicitly required by exactOptionalPropertyTypes — the
  // callsite passes ``job.stop_reason`` which is typed string | null | undefined.
  stopReason?: string | null | undefined;
  totalEvaluated?: number | null | undefined;
  // Server-supplied warning (e.g. the lexical-deadline partial message); shown
  // regardless of stop reason.
  notice?: string | null | undefined;
}

interface BannerContent {
  /** ARIA-friendly status text. */
  text: string;
  /** Optional supporting line surfaced as a tooltip / aria-description. */
  tooltip?: string;
  /** Visual category drives the tailwind classes. */
  variant: 'info' | 'warn';
}

function _bannerFor(
  reason: StopReason,
  totalEvaluated: number | null | undefined,
): BannerContent | null {
  switch (reason) {
    case 'max_matches':
      // Got enough good answers — the optimistic path. We intentionally
      // render NO banner here (same as `completed`): the blue "Showing top
      // N matches" info bar was removed as noise above the results.
      return null;
    case 'max_evaluated':
      // Partial-by-construction. Critical that the user can tell this
      // apart from max_matches — the implication is "we may have missed
      // relevant drugs we didn't reach", which is the Investigator's
      // "don't share wrong information" invariant in action.
      return {
        text:
          totalEvaluated != null
            ? `Showing matches found in the top ${totalEvaluated} candidates — more drugs may be relevant.`
            : 'Showing matches found in the top candidates — more drugs may be relevant.',
        tooltip:
          'The Investigator evaluated the most-relevant candidates first ' +
          'and stopped at the evaluation budget. To see further results, ' +
          'narrow the query (e.g. specify a therapeutic area or class).',
        variant: 'warn',
      };
    case 'time_budget_exceeded':
      return {
        text:
          totalEvaluated != null
            ? `Showing partial results — query timed out after evaluating ${totalEvaluated} drugs.`
            : 'Showing partial results — query timed out.',
        variant: 'warn',
      };
    case 'completed':
      // No banner — the implicit "complete" state. UI shows results as-is.
      return null;
    case 'cancelled':
    case 'no_candidates':
    case 'cohort_too_small':
      // These render through other paths (empty-state messages) — no
      // banner needed here.
      return null;
    default:
      return null;
  }
}

export const StopReasonBanner: React.FC<StopReasonBannerProps> = ({
  stopReason,
  totalEvaluated,
  notice,
}) => {
  const content: BannerContent | null = notice
    ? { text: notice, variant: 'warn' }
    : stopReason
      ? _bannerFor(stopReason as StopReason, totalEvaluated)
      : null;
  if (!content) return null;

  // Tailwind classes follow the existing project convention used by
  // ResearchPanel's progress messages — info → blue accent, warn →
  // amber accent. Keeps the look consistent with the surrounding UI.
  const baseClasses =
    'rounded-md border px-4 py-2 text-sm flex items-start gap-2 mb-3';
  const variantClasses =
    content.variant === 'warn'
      ? 'border-amber-300 bg-amber-50 text-amber-900'
      : 'border-blue-200 bg-blue-50 text-blue-900';

  return (
    <div
      role="status"
      aria-label={content.text}
      title={content.tooltip || content.text}
      className={`${baseClasses} ${variantClasses}`}
      data-testid="stop-reason-banner"
      data-stop-reason={stopReason}
    >
      <span className="flex-1">{content.text}</span>
    </div>
  );
};

export default StopReasonBanner;
